import { Link, useNavigate } from "react-router-dom";
import { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../i18n/I18nProvider";
import LanguageSwitcher from "./LanguageSwitcher";
import NotificationBell from "./NotificationBell";
import LockedModuleModal from "./LockedModuleModal";
import { Toaster } from "./ui/sonner";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "./ui/dropdown-menu";
import {
  Menu, ChevronDown, Package, Wrench, Box, Folder, Plus,
  LayoutDashboard, Leaf, ShieldCheck, LogIn, LogOut, User, Truck, Lock,
} from "lucide-react";

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [showLockedModal, setShowLockedModal] = useState(false);

  const handleLogin = () => {
    navigate("/login");
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const roleLabel = user?.role ? t(`role.${user.role}`) : t("nav.noRole");

  // Build navigation items. When the user is NOT logged in, all modules
  // remain visible but locked. Marketplace is always free.
  const navItems = [
    { to: "/marketplace", label: t("nav.material"), icon: Package, testid: "nav-marketplace-link", show: true },
    { to: "/machines", label: t("nav.machines"), icon: Wrench, testid: "nav-machines-link", show: true, requiresAuth: true },
    { to: "/vehicles", label: t("nav.vehicles"), icon: Truck, testid: "nav-vehicles-link", show: true, requiresAuth: true },
    { to: "/inventory", label: t("nav.inventory"), icon: Box, testid: "nav-inventory-link", show: true, requiresAuth: true },
    { to: "/projects", label: t("nav.projects"), icon: Folder, testid: "nav-projects-link", show: true, requiresAuth: true },
    { to: "/listings/new", label: t("nav.addListing"), icon: Plus, testid: "nav-add-listing-link", show: !user || user?.role === "seller" || user?.role === "admin", requiresAuth: true },
    { to: "/dashboard", label: t("nav.dashboard"), icon: LayoutDashboard, testid: "nav-dashboard-link", show: true, requiresAuth: true },
    { to: "/dashboard/co2e", label: t("nav.co2e"), icon: Leaf, testid: "nav-co2e-link", show: true, requiresAuth: true, accent: "moss" },
    { to: "/admin", label: "Admin", icon: ShieldCheck, testid: "nav-admin-link", show: !!user?.admin_eligible, accent: "brick" },
  ].filter((it) => it.show).map((it) => ({ ...it, locked: !!it.requiresAuth && !user }));

  return (
    <div className="min-h-screen flex flex-col">
      <header className="sticky top-0 z-40 bg-[var(--bone)] border-b border-[var(--ink-200)]">
        <div className="max-w-[1440px] mx-auto flex items-center justify-between px-4 sm:px-6 py-4 gap-3">
          <Link to="/" className="flex items-center gap-2 shrink-0 min-w-0" data-testid="nav-home-link">
            <div className="w-8 h-8 bg-[var(--ink)] text-[var(--bone)] grid place-items-center font-display font-bold text-lg shrink-0">R</div>
            <span className="font-display text-lg sm:text-xl font-semibold tracking-tight truncate">{t("brand.name")}</span>
          </Link>

          <div className="flex items-center gap-2 sm:gap-3 shrink-0">
            <LanguageSwitcher />
            {user && <NotificationBell />}

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className="inline-flex items-center gap-2 px-3 py-2 border border-[var(--ink-200)] bg-[var(--bone)] hover:bg-[var(--surface-alt)] text-xs font-mono-tech uppercase tracking-[0.1em]"
                  data-testid="nav-menu-trigger"
                  aria-label={t("nav.menu")}
                >
                  <Menu size={14} /> <span className="hidden sm:inline">{t("nav.menu")}</span> <ChevronDown size={12} />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                sideOffset={8}
                collisionPadding={8}
                className="w-64 max-w-[calc(100vw-1rem)] bg-[var(--bone)] border border-[var(--ink-200)] rounded-none shadow-lg"
                data-testid="nav-menu-content"
              >
                {user && (
                  <>
                    <div className="px-3 py-2.5 flex items-center gap-2 bg-[var(--surface-alt)] border-b border-[var(--ink-200)]">
                      <div className="w-7 h-7 bg-[var(--ink)] text-[var(--bone)] grid place-items-center shrink-0">
                        <User size={12} />
                      </div>
                      <div className="min-w-0">
                        <div className="font-display text-sm truncate" data-testid="nav-user-name">{user.name}</div>
                        <div className="label-tech truncate">{roleLabel}</div>
                      </div>
                    </div>
                  </>
                )}

                {navItems.map((it) => {
                  const Icon = it.icon;
                  const accentClass =
                    it.accent === "moss" ? "text-[var(--moss)]" :
                    it.accent === "brick" ? "text-[var(--brick)]" : "";

                  if (it.locked) {
                    return (
                      <DropdownMenuItem
                        key={it.to}
                        className="rounded-none focus:bg-[var(--surface-alt)] cursor-pointer"
                        onSelect={(e) => { e.preventDefault(); setShowLockedModal(true); }}
                      >
                        <button
                          type="button"
                          title={t("locked.hover")}
                          data-testid={`${it.testid}-locked`}
                          className={`flex items-center gap-3 px-3 py-2.5 text-sm w-full opacity-60 hover:opacity-90 ${accentClass}`}
                        >
                          <Icon size={14} />
                          <span className="font-mono-tech uppercase tracking-[0.08em] text-xs flex-1 text-left">{it.label}</span>
                          <Lock size={11} className="text-[var(--ink-500)]" />
                        </button>
                      </DropdownMenuItem>
                    );
                  }

                  return (
                    <DropdownMenuItem key={it.to} asChild className="rounded-none focus:bg-[var(--surface-alt)] cursor-pointer">
                      <Link to={it.to} data-testid={it.testid} className={`flex items-center gap-3 px-3 py-2.5 text-sm ${accentClass}`}>
                        <Icon size={14} />
                        <span className="font-mono-tech uppercase tracking-[0.08em] text-xs">{it.label}</span>
                      </Link>
                    </DropdownMenuItem>
                  );
                })}

                <DropdownMenuSeparator className="bg-[var(--ink-200)]" />

                {user ? (
                  <DropdownMenuItem
                    onSelect={handleLogout}
                    className="rounded-none focus:bg-[var(--surface-alt)] cursor-pointer flex items-center gap-3 px-3 py-2.5 text-sm text-[var(--brick)]"
                    data-testid="nav-logout-btn"
                  >
                    <LogOut size={14} />
                    <span className="font-mono-tech uppercase tracking-[0.08em] text-xs">{t("nav.signOut")}</span>
                  </DropdownMenuItem>
                ) : (
                  <DropdownMenuItem
                    onSelect={handleLogin}
                    className="rounded-none focus:bg-[var(--surface-alt)] cursor-pointer flex items-center gap-3 px-3 py-2.5 text-sm bg-[var(--brick)] text-[var(--bone)] focus:bg-[var(--brick)] focus:text-[var(--bone)]"
                    data-testid="nav-login-menu-btn"
                  >
                    <LogIn size={14} />
                    <span className="font-mono-tech uppercase tracking-[0.08em] text-xs">{t("nav.signIn")}</span>
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Desktop-only quick action — keeps the prominent CTA on wide screens */}
            {!user && (
              <button onClick={handleLogin} className="btn-primary hidden sm:inline-flex" data-testid="nav-login-btn">
                {t("nav.signIn")}
              </button>
            )}
          </div>
        </div>
      </header>

      <main className="flex-1">{children}</main>

      <footer className="border-t border-[var(--ink-200)] mt-16">
        <div className="max-w-[1440px] mx-auto px-6 py-8 flex items-center justify-between">
          <div className="label-tech">{t("nav.footer")}</div>
          <div className="label-tech">© {new Date().getFullYear()}</div>
        </div>
      </footer>
      <Toaster />
      {showLockedModal && (
        <LockedModuleModal onClose={() => setShowLockedModal(false)} onLogin={handleLogin} />
      )}
    </div>
  );
}
